import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { STORAGE_KEYS, getValue } from '../Storage';
import { toast } from 'react-toastify';
import { useCompanyStore } from '../company';
import { getUserCorporateId, isHRUser } from '../../utils/helper';
import { doGET } from '../../utils/HttpUtils';
import { ENDPOINTS } from '../api/constants';

const SESSION_EXPIRED_EVENT = "ewa:session-expired";

// Define the shape of the context value
interface AppContextProps {
  userData: any;
  setUserData: React.Dispatch<React.SetStateAction<any>>;
  success: (message: string) => any;
  error: (message: string) => any;
  verifyToken: (message: string) => any;
  isLoggedIn: boolean;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  isTokenVerified: boolean;
  setIsTokenVerified: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => void;
  refreshUserProfile: () => Promise<any>;
  token: string;
  setToken: React.Dispatch<React.SetStateAction<any>>;
}

// Define the props for the AppProvider component
interface AppProviderProps {
  children: ReactNode;
}

// Create the context with a default value
export const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | any>(null);
  const [token, setToken] = useState<string>("");

  const [isTokenVerified, setIsTokenVerified] = useState(false); // State flag for token verification
  const { setGlobalCompanyId } = useCompanyStore();

  const success = (message: string) => toast.success(message);
  const error = (message: string) => toast.error(message);

  const normalizeFrontendRole = (role?: string) => {
    if (role === "specialist" || role === "specialists") return "doctors";
    return role;
  };

  const applyUserProfile = (profileData: any) => {
    if (!profileData) {
      return null;
    }

    const existingStoredUserData = getValue(STORAGE_KEYS.USER_DATA);
    let previousUserData: any = userData;

    if (!previousUserData && existingStoredUserData) {
      try {
        previousUserData = JSON.parse(existingStoredUserData);
      } catch (_error) {
        previousUserData = null;
      }
    }

    const normalizedUserData = {
      ...previousUserData,
      ...profileData,
      backendRole: profileData?.userType || profileData?.role || previousUserData?.backendRole,
      role:
        normalizeFrontendRole(
          profileData?.role ||
          profileData?.userType ||
          previousUserData?.role
        ),
    };

    setUserData(normalizedUserData);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(normalizedUserData));

    if (isHRUser(normalizedUserData)) {
      const corporateId = getUserCorporateId(normalizedUserData);
      if (corporateId) {
        setGlobalCompanyId(corporateId);
      }
    }

    return normalizedUserData;
  };

  const refreshUserProfile = async () => {
    const response = await doGET(ENDPOINTS.profile);
    if (response.status >= 200 && response.status < 300) {
      const responseData = response.data?.data || response.data;
      const profileData =
        responseData?.user ||
        responseData?.profile ||
        responseData;

      return applyUserProfile(profileData);
    }

    return null;
  };

  const verifyToken = async (token: string | null) => {
    try {
      if (token) {
        setIsLoggedIn(true);
        return true;
      } else {
        setIsLoggedIn(false);
        localStorage.clear();
      }
    } catch (err) {
      error('Failed to verify token');
    } finally {
      setIsTokenVerified(true)
    }
    return false
  };

  const logout = () => {
    localStorage.clear();
    setUserData(null);
    setIsLoggedIn(false);
    setGlobalCompanyId("");
    success('Logout Successfully');
  };

  useEffect(() => {
    // Check if token exists in localStorage on app load
    const token = getValue(STORAGE_KEYS.TOKEN);
    if (token) {
      setIsLoggedIn(true);
      setIsTokenVerified(true);

      // Restore userData from localStorage so route guards have role info
      const storedUserData = getValue(STORAGE_KEYS.USER_DATA);
      if (storedUserData) {
        try {
          const parsedUserData = JSON.parse(storedUserData);
          applyUserProfile(parsedUserData);
        } catch (e) {
          console.error('Failed to parse stored user data:', e);
        }
      }

      refreshUserProfile().catch((profileError) => {
        console.error('Failed to refresh user profile:', profileError);
      });
    } else {
      setIsTokenVerified(true);
      setIsLoggedIn(false)
    }
  }, []);

  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      localStorage.clear();
      setUserData(null);
      setIsLoggedIn(false);
      setIsTokenVerified(true);
      setGlobalCompanyId("");
      error(customEvent.detail?.message || 'Your session has expired. Please log in again.');
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        userData,
        setUserData,
        success,
        error,
        isLoggedIn,
        setIsLoggedIn,
        logout,
        refreshUserProfile,
        token,
        setToken,
        verifyToken,
        isTokenVerified,
        setIsTokenVerified
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
